import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Preview of the reaction scheme(s) for the currently selected named reaction(s),
 * shown in the rxn_enum launch dialog so the user sees exactly what will be
 * enumerated before submitting.
 *
 * The SVGs are pre-rendered from the SMIRKS master (reaction_schemes.json), keyed
 * by the same names as RXN_ENUM_REACTIONS -- both generated from
 * named_reactions_SMIRKS_master_2col.csv, so a key can never drift from an option.
 * ~17 reactions have no representative SMIRKS or an unrenderable one (placeholder
 * atoms); those fall back to a "no preview" note. The map (~2.7 MB) is loaded
 * lazily via dynamic import so it stays out of the main bundle.
 *
 * @param {{reactions: string[]}} props selected reaction names (empty = all)
 */
export default function ReactionSchemePreview({ reactions }) {
    const [schemes, setSchemes] = useState(null);

    useEffect(() => {
        if (!reactions || reactions.length === 0 || schemes) return undefined;
        let alive = true;
        import('BMapsSrc/jobman/jobs/reaction_schemes.json')
            .then((m) => { if (alive) setSchemes(m.default || m); })
            .catch(() => { if (alive) setSchemes({}); });
        return () => { alive = false; };
    }, [reactions, schemes]);

    // Blank selection = "all reactions"; nothing to preview.
    if (!reactions || reactions.length === 0) return null;

    if (schemes === null) {
        return (
            <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                Loading reaction preview…
            </Typography>
        );
    }

    return (
        <Box sx={{
            width: '100%', mt: 1, maxHeight: 360, overflowY: 'auto',
        }}
        >
            {reactions.map((name) => (
                <Box key={name} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        {name}
                    </Typography>
                    {schemes[name] ? (
                        <Box
                            sx={{
                                background: '#fff',
                                borderRadius: 1,
                                p: 0.5,
                                '& svg': { maxWidth: '100%', height: 'auto', display: 'block' },
                            }}
                            /* SVG is our own RDKit-generated markup, not user input. */
                            dangerouslySetInnerHTML={{ __html: schemes[name] }}
                        />
                    ) : (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}
                        >
                            No scheme preview available for this reaction.
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}
